import React, { memo } from 'react';
import PropTypes from 'prop-types';

const SkeletonLoader = ({ 
  className = '',
  variant = 'rectangular',
  width = '100%',
  height = '20px',
  animation = 'pulse',
  children,
  ...props 
}) => {
  const baseClasses = 'bg-gray-200 rounded';
  
  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]',
    none: '',
  };

  const variantClasses = {
    text: 'h-4 rounded',
    rectangular: 'rounded',
    circular: 'rounded-full',
    card: 'rounded-lg',
  };

  const style = {
    width,
    height: variant === 'circular' ? width : height,
  };

  if (children) {
    return (
      <div className={`${baseClasses} ${animationClasses[animation]} ${className}`} style={style} {...props}>
        {children}
      </div>
    );
  }

  return (
    <div 
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
      {...props}
    />
  );
};

// Pre-built skeleton components for common use cases
const TextSkeletonComponent = ({ lines = 1, className = '', ...props }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonLoader
        key={index}
        variant="text"
        width={index === lines - 1 ? '75%' : '100%'}
        {...props}
      />
    ))}
  </div>
);

TextSkeletonComponent.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string
};

TextSkeletonComponent.defaultProps = {
  lines: 1,
  className: ''
};

export const TextSkeleton = memo(TextSkeletonComponent);

const CardSkeletonComponent = ({ className = '', ...props }) => (
  <div className={`p-4 border border-gray-200 rounded-lg ${className}`}>
    <div className="space-y-3">
      <SkeletonLoader variant="rectangular" height="200px" />
      <SkeletonLoader variant="text" height="24px" width="80%" />
      <TextSkeleton lines={2} />
      <div className="flex space-x-2">
        <SkeletonLoader variant="rectangular" height="32px" width="80px" />
        <SkeletonLoader variant="rectangular" height="32px" width="80px" />
      </div>
    </div>
  </div>
);

CardSkeletonComponent.propTypes = {
  className: PropTypes.string
};

CardSkeletonComponent.defaultProps = {
  className: ''
};

export const CardSkeleton = memo(CardSkeletonComponent);

const ButtonSkeletonComponent = ({ className = '', ...props }) => (
  <SkeletonLoader
    variant="rectangular"
    height="44px"
    width="120px"
    className={`rounded-lg ${className}`}
    {...props}
  />
);

ButtonSkeletonComponent.propTypes = {
  className: PropTypes.string
};

ButtonSkeletonComponent.defaultProps = {
  className: ''
};

export const ButtonSkeleton = memo(ButtonSkeletonComponent);

const AvatarSkeletonComponent = ({ size = '40px', className = '', ...props }) => (
  <SkeletonLoader
    variant="circular"
    width={size}
    height={size}
    className={className}
    {...props}
  />
);

AvatarSkeletonComponent.propTypes = {
  size: PropTypes.string,
  className: PropTypes.string
};

AvatarSkeletonComponent.defaultProps = {
  size: '40px',
  className: ''
};

export const AvatarSkeleton = memo(AvatarSkeletonComponent);

const ListItemSkeletonComponent = ({ className = '', ...props }) => (
  <div className={`flex items-center space-x-3 p-3 ${className}`}>
    <AvatarSkeleton size="48px" />
    <div className="flex-1 space-y-2">
      <SkeletonLoader variant="text" height="16px" width="60%" />
      <SkeletonLoader variant="text" height="14px" width="40%" />
    </div>
  </div>
);

ListItemSkeletonComponent.propTypes = {
  className: PropTypes.string
};

ListItemSkeletonComponent.defaultProps = {
  className: ''
};

export const ListItemSkeleton = memo(ListItemSkeletonComponent);

const GameSkeletonComponent = ({ className = '', ...props }) => (
  <div className={`bg-white rounded-2xl p-6 shadow-xl max-w-4xl mx-auto ${className}`}>
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center space-y-2">
        <SkeletonLoader variant="text" height="32px" width="60%" className="mx-auto" />
        <SkeletonLoader variant="text" height="16px" width="40%" className="mx-auto" />
      </div>
      
      {/* Question area */}
      <div className="space-y-4">
        <SkeletonLoader variant="rectangular" height="120px" className="rounded-lg" />
        
        {/* Options */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonLoader
              key={index}
              variant="rectangular"
              height="80px"
              className="rounded-lg"
            />
          ))}
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex justify-center space-x-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <ButtonSkeleton key={index} />
        ))}
      </div>
    </div>
  </div>
);

GameSkeletonComponent.propTypes = {
  className: PropTypes.string
};

GameSkeletonComponent.defaultProps = {
  className: ''
};

export const GameSkeleton = memo(GameSkeletonComponent);

const ProgressSkeletonComponent = ({ className = '', ...props }) => (
  <div className={`space-y-4 ${className}`}>
    <div className="flex justify-between items-center">
      <SkeletonLoader variant="text" height="20px" width="30%" />
      <SkeletonLoader variant="text" height="16px" width="20%" />
    </div>
    <SkeletonLoader variant="rectangular" height="8px" className="rounded-full" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="text-center space-y-2">
          <SkeletonLoader variant="circular" width="60px" className="mx-auto" />
          <SkeletonLoader variant="text" height="14px" width="80%" className="mx-auto" />
        </div>
      ))}
    </div>
  </div>
);

ProgressSkeletonComponent.propTypes = {
  className: PropTypes.string
};

ProgressSkeletonComponent.defaultProps = {
  className: ''
};

export const ProgressSkeleton = memo(ProgressSkeletonComponent);

SkeletonLoader.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['text', 'rectangular', 'circular', 'card']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  animation: PropTypes.oneOf(['pulse', 'wave', 'none']),
  children: PropTypes.node
};

SkeletonLoader.defaultProps = {
  className: '',
  variant: 'rectangular',
  width: '100%',
  height: '20px',
  animation: 'pulse',
  children: null
};

export default memo(SkeletonLoader);